import { useState } from "react";
import { toast } from "sonner";
import { Edit, Trash2, PlusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useFetchSaccoDeposits,
  useDeleteSaccoDeposit,
  type SaccoDeposit,
} from "@/hooks/sacco/useSaccoDeposits.ts";
import { DepositForm } from "./DepositForm";

interface DepositListProps {
  membershipId: string;
  nativeCurrency?: string;
  displayCurrency?: string;
  convert?: (amount: number, from: string, to: string) => number;
  isLoading?: boolean;
  error?: Error | null;
}

function formatCurrency(value: number, currency: string) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(value);
}

function formatDate(dateStr: string) {
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function DepositList({
  membershipId,
  nativeCurrency = 'KES',
  displayCurrency = 'KES',
  convert,
  isLoading: ratesLoading = false,
  error: ratesError = null,
}: DepositListProps) {
  const { data: deposits = [], isLoading } = useFetchSaccoDeposits(membershipId);
  const deleteDeposit = useDeleteSaccoDeposit(membershipId);

  const [formOpen, setFormOpen] = useState(false);
  const [editingDeposit, setEditingDeposit] = useState<SaccoDeposit | undefined>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const sorted = [...deposits].sort(
    (a, b) => new Date(a.deposit_date).getTime() - new Date(b.deposit_date).getTime()
  );

  function handleEdit(deposit: SaccoDeposit) {
    setEditingDeposit(deposit);
    setFormOpen(true);
  }

  function handleAddNew() {
    setEditingDeposit(undefined);
    setFormOpen(true);
  }

  function handleDeleteClick(id: string) {
    setDeletingId(id);
    setDeleteDialogOpen(true);
  }

  async function handleConfirmDelete() {
    if (!deletingId) return;
    try {
      await deleteDeposit.mutateAsync(deletingId);
      toast.success("Deposit deleted successfully");
    } catch {
      toast.error("Failed to delete deposit");
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={handleAddNew} size="sm">
          <PlusCircle className="h-4 w-4 mr-2" />
          Add Deposit
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground text-center py-4">Loading deposits...</p>
      ) : sorted.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">
          No deposits recorded yet. Add your first deposit above.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Note</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((deposit) => (
              <TableRow key={deposit.id}>
                <TableCell>{formatDate(deposit.deposit_date)}</TableCell>
                <TableCell className="text-right font-medium">
                  {ratesLoading ? (
                    <Skeleton className="h-4 w-20 inline-block" />
                  ) : (() => {
                    let displayValue = Number(deposit.amount);
                    if (!ratesError && convert && displayCurrency !== nativeCurrency) {
                      try {
                        displayValue = convert(displayValue, nativeCurrency, displayCurrency);
                      } catch {
                        // fall back to raw amount
                      }
                    }
                    return formatCurrency(displayValue, ratesError ? nativeCurrency : displayCurrency);
                  })()}
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {deposit.note ?? "—"}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(deposit)}
                      aria-label="Edit deposit"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => handleDeleteClick(deposit.id)}
                      disabled={deleteDeposit.isPending}
                      aria-label="Delete deposit"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <DepositForm
        membershipId={membershipId}
        nativeCurrency={nativeCurrency}
        deposit={editingDeposit}
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) setEditingDeposit(undefined);
        }}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Deposit</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this deposit? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
